import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { PaddingControl, BorderRadiusControl, StyleControl, SkinSelector } from '../CommonProperties';
import { usePropertyPanel } from '../PropertyPanelContext';

const CartProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { devicePropertyTab, getDevicePropertyValue } = usePropertyPanel();
  
  // 컴포넌트 속성 변경 핸들러 - 컴포넌트의 최상위 속성에 직접 저장
  const handleComponentPropsChange = (property, value) => {
    // skin 속성은 componentProps에 저장해야 함
    if (property === 'skin') {
      const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
      existingProps[property] = value;
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          componentProps: existingProps
        }
      }));
      return;
    }
    // 최상위 속성으로 업데이트
    const updatedComponent = JSON.parse(JSON.stringify(selectedComponent));
    
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 속성만 변경
    if (devicePropertyTab) {
      // 디바이스별 객체 초기화
      if (!updatedComponent.deviceProperty) {
        updatedComponent.deviceProperty = {
          pc: {},
          mobile: {}
        };
        
        // 기존 속성을 PC/모바일에 복사
        Object.keys(updatedComponent).forEach(key => {
          if (key !== 'deviceProperty' && !['id', 'type', 'content', 'style'].includes(key)) {
            updatedComponent.deviceProperty.pc[key] = updatedComponent[key];
            updatedComponent.deviceProperty.mobile[key] = updatedComponent[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!updatedComponent.deviceProperty[devicePropertyTab]) {
        updatedComponent.deviceProperty[devicePropertyTab] = {};
      }
      
      updatedComponent.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        updatedComponent[property] = value;
      }
    } else {
      // 최상위 속성에 직접 업데이트
      updatedComponent[property] = value;
      
      // 디바이스별 속성도 업데이트 (있는 경우)
      if (updatedComponent.deviceProperty) {
        if (updatedComponent.deviceProperty.pc) {
          updatedComponent.deviceProperty.pc[property] = value;
        }
        if (updatedComponent.deviceProperty.mobile) {
          updatedComponent.deviceProperty.mobile[property] = value;
        }
      }
    }
    
    // 컴포넌트 전체 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: updatedComponent
    }));
    
    // 전역 이벤트 발생 - 현재 속성값이 변경되었음을 알림
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('propertyChanged', {
        detail: {
          deviceType: devicePropertyTab || 'all',
          componentId: selectedComponent.id,
          property,
          value
        }
      }));
    }
  };

  // 장바구니 속성 가져오기
  const getValue = (property, defaultValue) => {
    // 디바이스별 속성값 먼저 확인
    if (devicePropertyTab && 
        selectedComponent.deviceProperty?.[devicePropertyTab]?.[property] !== undefined) {
      return selectedComponent.deviceProperty[devicePropertyTab][property];
    }
    
    // 최상위 속성 확인
    if (selectedComponent[property] !== undefined) {
      return selectedComponent[property];
    }
    
    return defaultValue;
  };

  return (
    <>
      {/* 스킨 선택 */}
      <SkinSelector
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => handleComponentPropsChange('skin', skinId)}
      />

      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3H5.5L6 5M6 5H21L17 14H8M6 5L8 14M8 14L6.5 19H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="21.5" r="1.5" stroke="currentColor"/>
            <circle cx="17.5" cy="21.5" r="1.5" stroke="currentColor"/>
          </svg>
          장바구니 설정
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>타이틀</label>
          <input 
            type="text"
            value={getValue('title', '장바구니')}
            onChange={(e) => handleComponentPropsChange('title', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>빈 장바구니 메시지</label>
          <input 
            type="text"
            value={getValue('emptyText', '장바구니가 비어 있습니다.')}
            onChange={(e) => handleComponentPropsChange('emptyText', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>배송비 (원)</label>
          <input 
            type="number"
            min="0"
            value={getValue('shippingFee', 4000)}
            onChange={(e) => handleComponentPropsChange('shippingFee', parseInt(e.target.value, 10))}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>재고 정보 표시</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-stock-cart"
              type="checkbox" 
              checked={getValue('showStock', false)}
              onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
            />
            <label htmlFor="toggle-stock-cart" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>무료배송 기준금액 (원)</label>
          <input 
            type="number"
            min="0"
            value={getValue('freeShippingThreshold', 50000)}
            onChange={(e) => handleComponentPropsChange('freeShippingThreshold', parseInt(e.target.value, 10))}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* 표시 옵션 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          표시 옵션
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>상품 이미지</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-image"
              type="checkbox" 
              checked={getValue('showImage', true)}
              onChange={(e) => handleComponentPropsChange('showImage', e.target.checked)}
            />
            <label htmlFor="toggle-show-image" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>수량 선택기</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-quantity-selector"
              type="checkbox" 
              checked={getValue('showQuantitySelector', true)}
              onChange={(e) => handleComponentPropsChange('showQuantitySelector', e.target.checked)}
            />
            <label htmlFor="toggle-quantity-selector" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>삭제 버튼</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-remove-button"
              type="checkbox" 
              checked={getValue('showRemoveButton', true)}
              onChange={(e) => handleComponentPropsChange('showRemoveButton', e.target.checked)}
            />
            <label htmlFor="toggle-remove-button" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>쿠폰 입력</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-coupon"
              type="checkbox" 
              checked={getValue('showCoupon', true)}
              onChange={(e) => handleComponentPropsChange('showCoupon', e.target.checked)}
            />
            <label htmlFor="toggle-show-coupon" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>주문 요약</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-summary"
              type="checkbox" 
              checked={getValue('showSummary', true)}
              onChange={(e) => handleComponentPropsChange('showSummary', e.target.checked)}
            />
            <label htmlFor="toggle-show-summary" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>결제 버튼</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-checkout"
              type="checkbox" 
              checked={getValue('showCheckout', true)}
              onChange={(e) => handleComponentPropsChange('showCheckout', e.target.checked)}
            />
            <label htmlFor="toggle-show-checkout" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
      </div>

      {/* 스타일 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          스타일 설정
        </div>
        
        <div className="property-row">
          <label>주요 버튼 색상</label>
          <input 
            type="color" 
            value={getValue('primaryButtonColor', '#007bff')}
            onChange={(e) => handleComponentPropsChange('primaryButtonColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
        
        <div className="property-row">
          <label>보조 버튼 색상</label>
          <input 
            type="color" 
            value={getValue('secondaryButtonColor', '#6c757d')}
            onChange={(e) => handleComponentPropsChange('secondaryButtonColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
        
        <div className="property-row">
          <label>삭제 버튼 색상</label>
          <input 
            type="color" 
            value={getValue('dangerButtonColor', '#dc3545')}
            onChange={(e) => handleComponentPropsChange('dangerButtonColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
        
        <div className="property-row">
          <label>가격 색상</label>
          <input 
            type="color" 
            value={getValue('priceColor', '#ff6b6b')}
            onChange={(e) => handleComponentPropsChange('priceColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
        
        <div className="property-row">
          <label>요약 배경색</label>
          <input 
            type="color" 
            value={getValue('summaryBackgroundColor', '#f8f9fa')}
            onChange={(e) => handleComponentPropsChange('summaryBackgroundColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
      </div>
      
      {/* <PaddingControl />
      <BorderRadiusControl />
      <StyleControl /> */}
    </>
  );
};

export default CartProperties; 